// Pure helpers for the `tap_ref` affiliate-attribution cookie. Extracted
// from `providers.js` so the SPA-navigation capture flow (Casper test
// 2026-05-12 — landed on `?ref=ntdhmgr` via direct browser URL, gave
// marketing consent, yet Stripe metadata lacked `ref`) can be tested
// without a DOM. The bug was that `Providers`' useEffect had `[]` as its
// dependency array, so the cookie-write ran only on cold mount; SPA
// navigation to a `?ref=` URL never re-triggered it. Fixed by moving
// the logic into `TapRefCapture` with `[pathname, searchParams]` deps.

const TAP_REF_SAFE_PATTERN = /[^A-Za-z0-9_-]/g;
const TAP_REF_MAX_LEN = 64;

// CookieConsent dispatches this after a visitor saves a choice. Route
// dependencies alone cannot observe localStorage changing while the visitor
// stays on the affiliate landing page, so attribution capture listens for
// this explicit consent-state transition as well.
export const COOKIE_CONSENT_UPDATED_EVENT =
  "favotrip:cookie-consent-updated";

/**
 * Read marketing-consent state from a storage-like object (e.g.
 * `window.localStorage` in the browser, or a Map-wrapping shim in
 * tests). Returns `true` only when the user has explicitly opted in.
 *
 * Reads two keys, in priority order:
 *   1. `cookie_consent_prefs` — granular JSON `{ marketing: bool, ... }`
 *      (current consent UX from 2026-04 onwards).
 *   2. `cookie_consent` — legacy accept-all sentinel string. Treated as
 *      consent only when value is the exact string `"accepted"`.
 *
 * Any storage exception or malformed JSON falls back to `false` — we
 * never assume consent under uncertainty (GDPR-safe default).
 *
 * @param {Pick<Storage, "getItem">} storage
 * @returns {boolean}
 */
export function readMarketingConsent(storage) {
  if (!storage || typeof storage.getItem !== "function") return false;
  try {
    const rawPrefs = storage.getItem("cookie_consent_prefs");
    if (rawPrefs) {
      try {
        const prefs = JSON.parse(rawPrefs);
        return prefs?.marketing === true;
      } catch {
        return false;
      }
    }
    return storage.getItem("cookie_consent") === "accepted";
  } catch {
    return false;
  }
}

/**
 * Extract a safe `tap_ref` value from a URL string, gated on marketing
 * consent. Returns the sanitised ref-code string when one should be
 * written, otherwise `null` (caller does not touch the cookie).
 *
 * The sanitisation matches the original `providers.js` behaviour:
 * strip everything outside `[A-Za-z0-9_-]`, then cap at 64 chars. We
 * trim to defend against accidentally writing arbitrary user input
 * (e.g. ` `, `&utm_*` continuation, encoded entities) into a cookie.
 *
 * @param {{ urlHref: string, marketingConsent: boolean }} params
 * @returns {string | null}
 */
export function resolveTapRefFromUrl({ urlHref, marketingConsent }) {
  if (!marketingConsent) return null;
  if (typeof urlHref !== "string" || !urlHref) return null;
  let parsed;
  try {
    parsed = new URL(urlHref);
  } catch {
    return null;
  }
  const ref = parsed.searchParams.get("ref");
  if (!ref) return null;
  const safe = ref.replace(TAP_REF_SAFE_PATTERN, "").slice(0, TAP_REF_MAX_LEN);
  return safe || null;
}

/**
 * Build the `Set-Cookie`-style attribute string for the `tap_ref` cookie.
 * Exposed so tests can assert on the exact value the browser will see.
 *
 * @param {string} safeRef sanitised ref-code (output of resolveTapRefFromUrl)
 * @returns {string}
 */
export function buildTapRefCookieString(safeRef) {
  return `tap_ref=${safeRef}; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
}
